export const MEMORY_MOODS = ["joy", "comfort", "curious", "ordinary"] as const;

export type MemoryMood = (typeof MEMORY_MOODS)[number];

export const MEMORY_OCCASIONS = [
	"solo",
	"together",
	"gift",
	"work",
	"routine",
] as const;

export type MemoryOccasion = (typeof MEMORY_OCCASIONS)[number];

export const PURCHASE_REASONS = [
	"needed_now",
	"planned",
	"curious",
	"treat",
	"together",
	"gift",
] as const;

export type PurchaseReason = (typeof PURCHASE_REASONS)[number];

export const PURCHASE_SATISFACTIONS = [
	"better_than_expected",
	"as_expected",
	"mixed",
	"not_for_me",
] as const;

export type PurchaseSatisfaction = (typeof PURCHASE_SATISFACTIONS)[number];

export const REPURCHASE_INTENTS = ["yes", "maybe", "no"] as const;

export type RepurchaseIntent = (typeof REPURCHASE_INTENTS)[number];

export interface MemoryRecord {
	receiptId: string;
	transactedAt: string;
	storeKey: string;
	storeDisplay: string;
	categoryId: string;
	memo?: string | null;
	mood?: MemoryMood | null;
	occasion?: MemoryOccasion | null;
	isFavorite?: boolean;
	purchaseReason?: PurchaseReason | null;
	expectedScene?: string | null;
	satisfaction?: PurchaseSatisfaction | null;
	repurchaseIntent?: RepurchaseIntent | null;
	afterNote?: string | null;
	reviewedAt?: string | null;
	deletedAt?: string | null;
}

export interface ReflectionCandidate extends MemoryRecord {
	ageDays: number;
	reason: "due" | "overdue";
}

export function findReflectionCandidates(
	records: MemoryRecord[],
	anchor = new Date(),
	minimumAgeDays = 3,
): ReflectionCandidate[] {
	const now = anchor.getTime();
	return records
		.filter((record) => {
			if (record.deletedAt || record.reviewedAt) return false;
			const time = Date.parse(record.transactedAt);
			return Number.isFinite(time) && now - time >= minimumAgeDays * 86400000;
		})
		.map((record) => {
			const ageDays = Math.floor(
				(now - Date.parse(record.transactedAt)) / 86400000,
			);
			return {
				...record,
				ageDays,
				reason: ageDays >= 14 ? ("overdue" as const) : ("due" as const),
			};
		})
		.sort(
			(a, b) =>
				Number(Boolean(b.purchaseReason || b.expectedScene)) -
					Number(Boolean(a.purchaseReason || a.expectedScene)) ||
				b.ageDays - a.ageDays ||
				a.receiptId.localeCompare(b.receiptId),
		);
}

export function findComparableMemories(
	record: MemoryRecord,
	records: MemoryRecord[],
	limit = 2,
): MemoryRecord[] {
	return records
		.filter(
			(candidate) =>
				candidate.receiptId !== record.receiptId &&
				!candidate.deletedAt &&
				(candidate.categoryId === record.categoryId ||
					candidate.storeKey === record.storeKey),
		)
		.sort((a, b) => {
			const storeAffinity =
				Number(b.storeKey === record.storeKey) -
				Number(a.storeKey === record.storeKey);
			return storeAffinity || b.transactedAt.localeCompare(a.transactedAt);
		})
		.slice(0, Math.max(0, limit));
}

export interface WeeklyMemoryStory {
	from: string;
	to: string;
	recordCount: number;
	uniqueStoreCount: number;
	revisitStore: string | null;
	highlight: MemoryRecord | null;
	title: string;
	body: string;
	prompt: string;
}

function startOfLocalDay(value: Date): Date {
	const result = new Date(value);
	result.setHours(0, 0, 0, 0);
	return result;
}

export function buildWeeklyMemoryStory(
	records: MemoryRecord[],
	anchor = new Date(),
): WeeklyMemoryStory {
	const to = startOfLocalDay(anchor);
	to.setDate(to.getDate() + 1);
	const from = new Date(to);
	from.setDate(from.getDate() - 7);
	const active = records
		.filter((record) => {
			if (record.deletedAt) return false;
			const time = Date.parse(record.transactedAt);
			return (
				Number.isFinite(time) && time >= from.getTime() && time < to.getTime()
			);
		})
		.sort((a, b) => b.transactedAt.localeCompare(a.transactedAt));
	const byStore = new Map<string, MemoryRecord[]>();
	for (const record of active) {
		const key = record.storeKey || record.storeDisplay;
		byStore.set(key, [...(byStore.get(key) ?? []), record]);
	}
	const revisit = [...byStore.values()]
		.filter((visits) => visits.length > 1)
		.sort(
			(a, b) =>
				b.length - a.length ||
				(b[0]?.transactedAt ?? "").localeCompare(a[0]?.transactedAt ?? ""),
		)[0];
	const highlight =
		active.find(
			(record) => record.isFavorite && Boolean(record.memo?.trim()),
		) ??
		active.find((record) => Boolean(record.memo?.trim())) ??
		active.find((record) => record.isFavorite) ??
		active[0] ??
		null;

	let title = "이번 주의 빈 페이지";
	let body = "아직 남긴 장면이 없어요. 결제 흔적 하나에 한 줄만 더해 보세요.";
	let prompt = "이번 주에 다시 떠올리고 싶은 소비는 무엇인가요?";
	if (active.length === 1 && highlight) {
		title = `${highlight.storeDisplay}, 이번 주의 첫 장면`;
		body = highlight.memo?.trim()
			? `이번 주에 남긴 한 줄: “${highlight.memo.trim()}”`
			: "장소와 시간은 모였어요. 한 줄을 더하면 나만의 장면이 됩니다.";
		prompt = "그때 누구와 있었고, 어떤 기분이었나요?";
	} else if (active.length > 1) {
		title = `${active.length}개의 소비가 한 주의 장면이 됐어요`;
		body = revisit?.[0]
			? `${revisit[0].storeDisplay}에 ${revisit.length}번 다시 갔고, ${byStore.size}곳의 기억을 남겼어요.`
			: `${byStore.size}곳을 오가며 서로 다른 장면을 남겼어요.`;
		prompt = highlight?.memo?.trim()
			? `이번 주의 한 문장: ${highlight.memo.trim()}`
			: "가장 마음에 남은 한 곳에 짧은 메모를 붙여 보세요.";
	}

	return {
		from: from.toISOString(),
		to: new Date(to.getTime() - 1).toISOString(),
		recordCount: active.length,
		uniqueStoreCount: byStore.size,
		revisitStore: revisit?.[0]?.storeDisplay ?? null,
		highlight,
		title,
		body,
		prompt,
	};
}

export function groupMemoryTimeline(records: MemoryRecord[]) {
	const active = records
		.filter((record) => !record.deletedAt)
		.sort((a, b) => b.transactedAt.localeCompare(a.transactedAt));
	const storeCounts = active.reduce<Map<string, number>>((counts, record) => {
		const key = record.storeKey || record.storeDisplay;
		counts.set(key, (counts.get(key) ?? 0) + 1);
		return counts;
	}, new Map());
	return active.map((record) => ({
		...record,
		visitCount: storeCounts.get(record.storeKey || record.storeDisplay) ?? 1,
		isRevisit:
			(storeCounts.get(record.storeKey || record.storeDisplay) ?? 1) > 1,
		monthKey: record.transactedAt.slice(0, 7),
	}));
}
